import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

// Backend sends datetime as plain 'YYYY-MM-DDTHH:mm' strings (no Z, no offset)
// Just reformat for display — no timezone conversion needed
const fmt = dt => {
  if (!dt) return '—';
  const [date, time] = dt.split('T');
  if (!date) return '—';
  const [y, m, d] = date.split('-');
  if (!time) return `${d}/${m}/${y}`;
  const [hh, mm] = time.split(':');
  const h = parseInt(hh);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${d}/${m}/${y}, ${String(h12).padStart(2,'0')}:${mm} ${ampm}`;
};
// Calculate duration from plain 'YYYY-MM-DDTHH:mm' strings
const dur = (s, e) => {
  if (!s || !e) return '—';
  const toMins = str => {
    const [date, time] = str.split('T');
    if (!date || !time) return 0;
    const [y, mo, d] = date.split('-').map(Number);
    const [h, m] = time.split(':').map(Number);
    return new Date(y, mo-1, d, h, m).getTime() / 60000;
  };
  const mins = Math.round(toMins(e) - toMins(s));
  if (mins < 0) return '—';
  return mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h ${mins%60}m`;
};

const RESOLVED_STATUSES = ['Resolved','Resolved - Vendor Case','Resolved - 3 Remainders','Resolved - Known Issues','Resolved - Feature Request','Resolved - Workaround'];
const isResolved = s => RESOLVED_STATUSES.includes(s);

function Field({ label, value }) {
  return (
    <div style={{minWidth:0}}>
      <div style={{fontSize:11,color:'#888',fontWeight:500,marginBottom:3}}>{label}</div>
      <div style={{fontSize:13,color:'#1a1a2e',wordBreak:'break-word',overflowWrap:'anywhere'}}>{value||'—'}</div>
    </div>
  );
}

export default function ViewTicket() {
  const { ticket_id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();

  useEffect(() => {
    api.get(`/tickets/${ticket_id}`)
      .then(({data:d}) => setData(d))
      .catch(() => navigate('/tickets'));
  }, [ticket_id]);

  if (!data) return (
    <Layout title="View Ticket">
      <div style={{textAlign:'center',padding:60,color:'#aaa'}}>
        <i className="fa-solid fa-spinner fa-spin" style={{fontSize:28}}></i>
      </div>
    </Layout>
  );

  const t = data.ticket;
  const resolved = isResolved(t.status);

  // Edit: admin always; user only if ATS consultant AND not resolved
  const canEdit = isAdmin || (
    user?.role==='user' &&
    !resolved &&
    t.ats_consultant?.trim().toLowerCase() === user?.full_name?.trim().toLowerCase()
  );

  const statusColor = resolved ? 'badge-resolved'
    : t.status==='Open' ? 'badge-open'
    : t.status==='In Progress' ? 'badge-progress'
    : 'badge-closed';

  const priorityColor = {Low:'badge-low',Medium:'badge-medium',High:'badge-high',Critical:'badge-critical'};

  return (
    <Layout title={`Ticket — ${ticket_id}`}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <span style={{fontFamily:'monospace',fontWeight:600,fontSize:16,color:'#6382e6'}}>{t.ticket_id}</span>
          <span className={`badge ${priorityColor[t.priority]||'badge-medium'}`}>{t.priority}</span>
          <span className={`badge ${statusColor}`}>{t.status}</span>
          {resolved && (
            <span style={{fontSize:11,color:'#3B6D11',background:'#EAF3DE',padding:'3px 8px',borderRadius:99}}>
              <i className="fa-solid fa-lock" style={{marginRight:4}}></i>Locked
            </span>
          )}
        </div>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-secondary" onClick={()=>navigate('/tickets')}>
            <i className="fa-solid fa-arrow-left"></i> Back
          </button>
          {canEdit && (
            <button className="btn btn-primary" onClick={()=>navigate(`/tickets/${ticket_id}/edit`)}>
              <i className="fa-solid fa-pen"></i> Edit
            </button>
          )}
          {resolved && !isAdmin && (
            <span style={{fontSize:12,color:'#aaa',alignSelf:'center'}}>
              <i className="fa-solid fa-circle-info" style={{marginRight:4}}></i>
              Resolved tickets are read-only
            </span>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{fontSize:15,fontWeight:600,marginBottom:16,color:'#1a1a2e'}}>{t.issue_short_desc}</div>

        {/* Strict 4-column grid — all rows aligned */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'16px 20px'}}>
          <Field label="Client"            value={t.client}/>
          <Field label="Support Plan"      value={t.support_plan}/>
          <Field label="ATS Consultant"    value={t.ats_consultant}/>
          <Field label="Sales Manager"     value={t.sales_manager}/>

          <Field label="Product Type"      value={t.product_type}/>
          <Field label="Product Version"   value={t.product_version}/>
          <Field label="Platform"          value={t.platform}/>
          <Field label="OS"                value={t.operating_system}/>

          <Field label="Classification"    value={t.classification}/>
          <Field label="Category"          value={t.category}/>
          <Field label="Opened On"         value={fmt(t.opened_on)}/>
          <Field label="Opened By (Name)"  value={t.opened_by_name}/>

          <Field label="Opened By (Email)" value={t.opened_by_email}/>
          <Field label="GTL Responded"     value={fmt(t.gtl_responded_on)}/>
          <Field label="Last Updated"      value={fmt(t.latest_update_dt)}/>
          <div/>
        </div>
      </div>

      {t.issue_detail_desc && (
        <div className="card">
          <div className="card-title"><i className="fa-solid fa-file-lines"></i> Issue Detail</div>
          <p style={{fontSize:13,lineHeight:1.7,color:'#333',whiteSpace:'pre-wrap'}}>{t.issue_detail_desc}</p>
        </div>
      )}
      {t.latest_update_note && (
        <div className="card">
          <div className="card-title"><i className="fa-solid fa-clock-rotate-left"></i> Latest Update</div>
          <p style={{fontSize:13,color:'#333'}}>{t.latest_update_note}</p>
        </div>
      )}
      {t.resolution_desc && (
        <div className="card">
          <div className="card-title"><i className="fa-solid fa-circle-check" style={{color:'#5DCAA5'}}></i> Resolution</div>
          <p style={{fontSize:13,lineHeight:1.7,color:'#333',whiteSpace:'pre-wrap'}}>{t.resolution_desc}</p>
        </div>
      )}
      {t.remarks && (
        <div className="card">
          <div className="card-title"><i className="fa-solid fa-note-sticky"></i> Remarks</div>
          <p style={{fontSize:13,color:'#333'}}>{t.remarks}</p>
        </div>
      )}

      <div className="card">
        <div className="card-title"><i className="fa-solid fa-phone"></i> Call Details ({data.calls?.length||0})</div>
        {!data.calls?.length ? (
          <div className="empty-state" style={{padding:20}}>
            <i className="fa-solid fa-phone-slash"></i><div>No call entries recorded</div>
          </div>
        ) : (
          <div style={{overflowX:'auto'}}>
            <table>
              <thead>
                <tr>
                  <th>#</th><th>R&D Start</th><th>R&D End</th><th>R&D Dur.</th>
                  <th>Call Start</th><th>Call End</th><th>Call Dur.</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {data.calls.map((c,i)=>(
                  <tr key={c.id}>
                    <td style={{color:'#888'}}>{i+1}</td>
                    <td style={{fontSize:12}}>{fmt(c.rd_start_dt)}</td>
                    <td style={{fontSize:12}}>{fmt(c.rd_end_dt)}</td>
                    <td><span className="badge badge-open">{dur(c.rd_start_dt,c.rd_end_dt)}</span></td>
                    <td style={{fontSize:12}}>{fmt(c.call_start_dt)}</td>
                    <td style={{fontSize:12}}>{fmt(c.call_end_dt)}</td>
                    <td><span className="badge badge-resolved">{dur(c.call_start_dt,c.call_end_dt)}</span></td>
                    <td style={{fontSize:12,color:'#555'}}>{c.call_notes||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}